/**
 * resolveGlance — the ONE place the live schedule becomes The Glance.
 *
 * Two projections of the same governing moment (the focus day's next timed
 * move), because two surfaces have opposite privacy postures:
 *
 *  - resolveGlance() → a RICH `GlanceModel` for the IN-APP preview. That runs on
 *    the principal's own screen, so it may show human labels ("Mandragora",
 *    "Substation Seattle").
 *  - toTDGlance() → the SCRUBBED `TDGlance` for the NATIVE lock-screen / widget.
 *    ADR-014, non-negotiable: a lockable/shared surface carries CODES + TIMES
 *    only — never a client / venue / person name. The scrub re-derives its lines
 *    from the entry; it does NOT pass the rich labels through.
 *
 * Calm "next move" only (the whisper/voice engine is a deliberate follow-up):
 * both return null when there is no timed move to count down to — honest, never
 * a fake countdown.
 */
import { registry } from "@td/registry";
import { t, type Locale } from "../i18n.ts";
import { computeVerdict, instantMs, type TodayFocus } from "../views/Today.tsx";
import { buildDayRunEntries, entryLabel, entryPlace, type DayRunEntry } from "../views/dayRun.ts";
import { buildGaps } from "../views/gaps.ts";
import { gigDisplayName } from "../views/GigList.tsx";
import type { TDGlance } from "../native/bridge.ts";
import type { GlanceModel } from "./GlanceSurfaces.tsx";

const sv = (v: unknown): string => (typeof v === "string" ? v : "");
const norm = (x: string): string =>
  x
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

/** Generic show-day activities safe to name on a lockable surface (they are
 * activity types, not a client/venue/person). Anything else → a generic label. */
const GENERIC_ACTIVITIES = new Set([
  "soundcheck", "sound check", "balance", "balances", "doors", "portes", "set", "dj set",
  "load-in", "load in", "loadin", "load-out", "load out", "loadout", "lobby call", "lobby",
  "checkout", "check-out", "check out", "check-in", "check in", "pickup", "pick-up", "pick up",
  "departure", "depart", "arrival", "arrivee", "rehearsal", "repetition", "dinner", "diner",
  "lunch", "dejeuner", "breakfast", "petit dejeuner", "press", "presse", "interview", "meet & greet",
]);

/** The absolute epoch ms of the focus day's next timed move, or null. */
function governingInstant(focus: TodayFocus): { next: DayRunEntry; eta: number } | null {
  const next = focus.next;
  if (next === null || next.time === "") return null;
  const eta = instantMs(focus.tz, next.day, next.time);
  return eta === null ? null : { next, eta };
}

/** RICH model for the in-app preview (full human labels allowed). */
export function resolveGlance(focus: TodayFocus, locale: Locale): GlanceModel | null {
  const g = governingInstant(focus);
  if (g === null) return null;
  const city = sv(focus.gig.state.gig?.fields["city"]) || gigDisplayName(focus.gig);
  return {
    source: t(locale, "glance.source_tour"),
    next: {
      label: entryLabel(g.next) || t(locale, "glance.next_label"),
      place: entryPlace(g.next) || city,
      etaEpochMs: g.eta,
    },
    whisper: null, // calm-first; the delta whisper is a follow-up
  };
}

function accentFor(collection: string): TDGlance["accent"] {
  if (collection === registry.travel.key) return "transit";
  if (collection === registry.hotels.key) return "lodging";
  if (collection === registry.schedule.key || collection === registry.timetable.key) return "venue";
  return "dayoff";
}

/** ADR-014 scrub: codes + times only, never a name. */
function scrubbedLine1(e: DayRunEntry, locale: Locale): string {
  if (e.collection === registry.travel.key) {
    const tv = registry.travel.fields;
    const ref = sv(e.item.fields[tv.ref.key]);
    const dep = sv(e.item.fields[tv.dep_iata.key]);
    const arr = sv(e.item.fields[tv.arr_iata.key]);
    const route = [dep, arr].filter(Boolean).join("→"); // IATA CODES only, never place names
    const coded = [ref, route].filter(Boolean).join(" · ");
    return coded || sv(e.item.fields[tv.kind.key]).toUpperCase() || t(locale, "glance.native_travel");
  }
  if (e.collection === registry.schedule.key) {
    const title = sv(e.item.fields[registry.schedule.fields.title.key]);
    return GENERIC_ACTIVITIES.has(norm(title)) ? title : t(locale, "glance.native_schedule");
  }
  if (e.collection === registry.timetable.key) return t(locale, "glance.native_set"); // the act is an identity — never crosses
  if (e.collection === registry.hotels.key) return t(locale, "glance.native_hotel"); // never the hotel name
  return t(locale, "glance.native_schedule");
}

/** An at-risk connection INTO the governing leg — the native "one decision"
 * (gap_risk). Only for today (a tight connection is a now-decision, not a
 * days-out warning). Reuses buildGaps' kind-aware thresholds; gapPlace is the
 * departure IATA CODE of the upcoming leg (ADR-014 — never a city name). */
function gapRiskInto(focus: TodayFocus): { slackMin: number; place: string } | null {
  const next = focus.next;
  if (!focus.isToday || next === null || next.collection !== registry.travel.key || next.time === "") return null;
  const chain = buildDayRunEntries(focus.gig).filter((e) => e.day !== "");
  const idx = chain.findIndex((e) => e.itemId === next.itemId && e.collection === next.collection);
  if (idx <= 0) return null;
  const prev = chain[idx - 1]!;
  if (prev.time === "") return null; // no measurable incoming gap
  const gaps = buildGaps(chain.slice(0, idx + 1), focus.tz); // last gap = the incoming connection
  const g = gaps[gaps.length - 1];
  if (g === undefined || g.status !== "at_risk") return null;
  return { slackMin: Math.max(0, Math.round(g.slackMinutes)), place: sv(next.item.fields[registry.travel.fields.dep_iata.key]).toUpperCase() };
}

/** SCRUBBED native payload (ADR-014). null when there's no timed move. */
export function toTDGlance(focus: TodayFocus, nowMs: number, locale: Locale): TDGlance | null {
  const g = governingInstant(focus);
  if (g === null) return null;
  const base = {
    primaryTime: g.eta,
    line1: scrubbedLine1(g.next, locale),
    line2: g.next.time, // "14:05" — always safe
    accent: accentFor(g.next.collection),
  };
  // A tight connection is THE decision — it outranks leave/next (reimagine plan).
  const gap = gapRiskInto(focus);
  if (gap !== null) {
    return { ...base, state: "gap_risk", gapSlackMin: gap.slackMin, gapPlace: gap.place || undefined };
  }
  const v = computeVerdict(nowMs, focus.tz, { day: focus.day }, g.next);
  const leaving = v !== null && (v.key === "today.verdict.leave" || v.key === "today.verdict.now");
  return { ...base, state: leaving ? "leave_now" : "next" };
}
